import z from "zod"
import type { JSX } from "react"

namespace Decoder {
  const baseMetricsItemSchema = z.object({
    metricData: z.record(z.string(), z.number().nullable()),
  })

  export const campaignMetricsItemSchema = baseMetricsItemSchema.extend({
    campaignId: z.string(),
  })

  export const agentMetricsItemSchema = baseMetricsItemSchema.extend({
    agentId: z.string(),
  })

  export const bilingualStringSchema = z.object({
    en: z.string(),
    ja: z.string(),
  })

  export const cacheDetailsSchema = z.object({
    cacheKey: z.string().nullable(),
    cacheSetTimestamp: z.string(),
  })
  export type CacheDetails = z.infer<typeof cacheDetailsSchema>

  const agentInfoMetadataItemSchema = z
    .object({
      FirstName: z.string().nullable(),
      LastName: z.string().nullable(),
      Username: z.string().nullable(),
    })
    .nullable()

  export const metadataItemSchema = z.object({
    name: bilingualStringSchema,
    additionalInfo: bilingualStringSchema,
    isInterval: z.boolean(),
    metricId: z.string(),
    parentId: z.string(),
    metricType: bilingualStringSchema,
  })

  export const campaignMetricsResponseSchema = z.object({
    data: z.object({
      metrics: z.array(campaignMetricsItemSchema),
      metadata: z.record(z.string(), metadataItemSchema),
      parentMetricsMetadata: z.record(z.string(), bilingualStringSchema),
      cacheDetails: cacheDetailsSchema,
    }),
  })

  export const campaignBreakdownMetricsResponseSchema = z.object({
    data: z.object({
      metrics: z.array(campaignMetricsItemSchema),
      metadata: z.record(z.string(), metadataItemSchema),
      parentMetricsMetadata: z.record(z.string(), bilingualStringSchema),
      agentMetadata: z
        .record(z.string(), agentInfoMetadataItemSchema)
        .nullable(),
      cacheDetails: cacheDetailsSchema,
    }),
  })

  export const agentMetricsResponseSchema = z.object({
    data: z.object({
      agentMetadata: z.record(z.string(), agentInfoMetadataItemSchema),
      metrics: z.array(agentMetricsItemSchema),
      metadata: z.record(z.string(), metadataItemSchema),
      parentMetricsMetadata: z.record(z.string(), bilingualStringSchema),
      cacheDetails: cacheDetailsSchema,
    }),
  })

  export const csvExportResponseSchema = z.object({
    data: z.string(),
  })

  export type BaseMetricsItem = z.infer<typeof baseMetricsItemSchema>
  export type CampaignMetricItem = z.infer<typeof campaignMetricsItemSchema>
  // TODO: Not confident about this type; Check with @ywtaoo if Record is similar to RecordType in zod
  export type ParentMetaData = Record<
    string,
    z.infer<typeof bilingualStringSchema>
  >
  export type MetaDataItem = z.infer<typeof metadataItemSchema>
  export type MetaData = Record<string, MetaDataItem>
  export type CampaignMetricsResponse = z.infer<
    typeof campaignMetricsResponseSchema
  >
  export type AgentMetricsResponse = z.infer<typeof agentMetricsResponseSchema>
  export type AgentMetricsItem = z.infer<typeof agentMetricsItemSchema>
  export type AgentInfoMetadata = Record<
    string,
    z.infer<typeof agentInfoMetadataItemSchema>
  >
  export type CellRendererKey = "average" | "ratio" | "total"

  export type DisplayAgentMetric = {
    fullName: JSX.Element | string
  } & Decoder.AgentMetricsItem
}

export { Decoder }
